import React, { useState } from 'react';
import AnimatedSection from '../../components/blocks/AnimatedSection';
import { getCortexEventBySlug } from '../../config/cortexEvents';
import { getEventImagePath, getEventDocumentPath } from '../../config/eventPaths';

const WORKSHOP_SLUG = 'workshop-2026';

const INSTAGRAM_POST_URL =
    'https://www.instagram.com/p/DWt2s6-iaUL/?utm_source=ig_web_copy_link&igsh=MzRlODBiNWFlZA==';

const SLIDES_FILENAME = 'FROM PROJECT TO CONFERENCE 101  Abstract and Presentation Workshop.pdf';

const SLIDES_THUMBNAIL_FILENAME =
    'FROM PROJECT TO CONFERENCE 101  Abstract and Presentation Workshop-1 (dragged).png';

/** Recap photos (same folder as poster assets). */
const RECAP_IMAGE_PATHS = [
    getEventImagePath('cortex', WORKSHOP_SLUG, '658010604_17873592411584841_770335800272245429_n.webp'),
    getEventImagePath('cortex', WORKSHOP_SLUG, '658325786_17873592420584841_8806010627142121517_n.webp'),
    getEventImagePath('cortex', WORKSHOP_SLUG, '659481773_17873592429584841_7505658383051482308_n.webp'),
];

const Workshop2026 = () => {
    const event = getCortexEventBySlug(WORKSHOP_SLUG);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [slidesThumbError, setSlidesThumbError] = useState(false);

    const slidesPath = getEventDocumentPath('cortex', WORKSHOP_SLUG, SLIDES_FILENAME);
    const slidesPathEncoded = encodeURI(slidesPath);
    const slidesThumbnailPath = getEventImagePath('cortex', WORKSHOP_SLUG, SLIDES_THUMBNAIL_FILENAME);
    const slidesThumbnailEncoded = encodeURI(slidesThumbnailPath);

    const nextImage = () => {
        setCurrentImageIndex((prev) => (prev + 1) % RECAP_IMAGE_PATHS.length);
    };

    const prevImage = () => {
        setCurrentImageIndex((prev) => (prev - 1 + RECAP_IMAGE_PATHS.length) % RECAP_IMAGE_PATHS.length);
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-16">
                    {event?.posterImage && (
                        <div className="mb-8 flex justify-center">
                            <img
                                src={event.posterImage}
                                alt={event.posterAlt || 'From Project to Conference 101 Workshop Poster'}
                                className="w-auto h-auto max-w-md md:max-w-lg object-contain rounded-lg shadow-lg"
                            />
                        </div>
                    )}
                    <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mt-8 mb-4">
                        From Project to Conference 101
                    </h1>
                    <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto mb-6">
                        Abstract and Presentation Workshop
                    </p>
                    <p className="text-lg text-gray-700 max-w-2xl mx-auto mb-6">
                        {event?.posterSubtitle || '26 March 2026, 12:00–1:00 PM · Common Core Lounge'}
                    </p>
                    <p className="inline-block px-4 py-2 rounded-full bg-amber-100 text-amber-900 font-semibold text-sm mb-6">
                        Workshop completed
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                        <a
                            href={event?.ctaHref || INSTAGRAM_POST_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block px-6 py-3 rounded-full border border-amber-400 text-amber-700 font-semibold hover:bg-amber-50 hover:border-amber-500 transition"
                        >
                            {event?.ctaLabel || 'View Instagram recap'}
                        </a>
                        <a
                            href="#workshop-recap"
                            className="inline-block px-6 py-3 rounded-full border border-gray-300 text-gray-700 font-semibold hover:border-amber-400 hover:text-amber-700 transition"
                        >
                            Recap, photos &amp; slides
                        </a>
                    </div>
                </div>

                {/* Overview */}
                <AnimatedSection
                    className="mb-16"
                    heading={<h2 className="text-3xl font-semibold text-gray-900 mb-8">About the Workshop</h2>}
                >
                    <div className="bg-white rounded-lg shadow-md p-8">
                        <p className="text-lg text-gray-700 leading-relaxed text-justify mb-6">
                            Have you ever looked at your final project and thought it could be something more? Most
                            undergraduate research is better than it looks on a grade sheet!
                        </p>
                        <p className="text-lg text-gray-700 leading-relaxed text-justify mb-6">
                            Join us for <strong>From Project to Conference 101: Abstract and Presentation Workshop</strong>,
                            supported by the Leadership and Impact Award by Common Core. We&apos;ll help you take work
                            you&apos;ve already done and shape it into a submission-ready abstract and presentation. No
                            prior conference experience needed.
                        </p>
                        <p className="text-lg text-gray-700 leading-relaxed text-justify">
                            Led by research postgraduates, this workshop will help you:
                        </p>
                        <ul className="list-disc list-inside space-y-2 text-lg text-gray-700 mt-4 mb-6">
                            <li>Distill your research into a compelling abstract</li>
                            <li>Structure a clear and engaging presentation</li>
                            <li>Leave with a submission-ready draft built from your own work</li>
                        </ul>
                        <p className="text-lg text-gray-700 leading-relaxed text-justify">
                            Open to students from all faculties. We recommend bringing a term paper or project you&apos;d
                            like to develop. Seats are limited—register early to secure your spot.
                        </p>
                    </div>
                </AnimatedSection>

                {/* Details */}
                <AnimatedSection
                    className="mb-16"
                    heading={<h2 className="text-3xl font-semibold text-gray-900 mb-8">Event Details</h2>}
                >
                    <div className="bg-white rounded-lg shadow-md p-8">
                        <div className="grid md:grid-cols-2 gap-6">
                            {event?.details?.map((d, i) => (
                                <div
                                    key={i}
                                    className="p-6 border-l-4 border-amber-500 rounded-lg bg-amber-50/50 hover:bg-amber-50 transition-colors"
                                >
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{d.label}</h3>
                                    <p className="text-gray-700">{d.value}</p>
                                </div>
                            ))}
                        </div>
                        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                            <a
                                href={INSTAGRAM_POST_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block px-6 py-3 rounded-full border-2 border-amber-500 text-amber-700 font-semibold hover:bg-amber-50 transition"
                            >
                                View Instagram recap
                            </a>
                            <a
                                href="#workshop-recap"
                                className="inline-block px-6 py-3 rounded-full border border-gray-300 text-gray-700 font-semibold hover:border-amber-400 hover:text-amber-700 transition"
                            >
                                Photos &amp; slides
                            </a>
                        </div>
                    </div>
                </AnimatedSection>

                {/* Recap — pattern aligned with Mongolian Critical Zone trip page */}
                <AnimatedSection
                    id="workshop-recap"
                    className="mb-16 scroll-mt-24"
                    heading={<h2 className="text-3xl font-semibold text-gray-900 mb-8">Workshop Recap</h2>}
                >
                    <div className="bg-white rounded-lg shadow-md p-8">
                        {/* Instagram-style card */}
                        <div className="max-w-3xl mx-auto mb-14">
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Instagram Post</h3>
                            <div className="flex items-center mb-4">
                                <a
                                    href="https://www.instagram.com/cortex.hku/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center hover:opacity-80 transition-opacity"
                                >
                                    <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-gray-200 bg-white flex items-center justify-center">
                                        <img
                                            src="/asset/icons/CORTEX_Logo_Radial.svg"
                                            alt="CORTEX"
                                            className="w-8 h-8 object-contain"
                                        />
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm font-semibold text-gray-900">cortex.hku</p>
                                        <p className="text-xs text-gray-500">@cortex.hku · Instagram</p>
                                    </div>
                                </a>
                            </div>

                            <div className="relative mb-4 border border-gray-200 rounded-xl overflow-hidden bg-white">
                                <div className="relative flex items-center justify-center bg-gray-50">
                                    <img
                                        src={RECAP_IMAGE_PATHS[currentImageIndex]}
                                        alt={`Workshop recap — ${currentImageIndex + 1} of ${RECAP_IMAGE_PATHS.length}`}
                                        className="w-full h-auto object-contain max-h-[min(70vh,640px)]"
                                    />
                                    {RECAP_IMAGE_PATHS.length > 1 && (
                                        <>
                                            <button
                                                type="button"
                                                onClick={prevImage}
                                                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg transition-all duration-200 z-10"
                                                aria-label="Previous image"
                                            >
                                                <svg className="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                                </svg>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={nextImage}
                                                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg transition-all duration-200 z-10"
                                                aria-label="Next image"
                                            >
                                                <svg className="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                </svg>
                                            </button>
                                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2 z-10 bg-black/30 backdrop-blur-sm px-3 py-1.5 rounded-full">
                                                {RECAP_IMAGE_PATHS.map((_, index) => (
                                                    <button
                                                        key={index}
                                                        type="button"
                                                        onClick={() => setCurrentImageIndex(index)}
                                                        className={`h-2 rounded-full transition-all duration-200 ${
                                                            index === currentImageIndex ? 'bg-white w-6' : 'bg-white/50 w-2'
                                                        }`}
                                                        aria-label={`Go to image ${index + 1}`}
                                                    />
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="border border-gray-200 rounded-xl overflow-hidden">
                                <div className="p-4 space-y-3 text-sm leading-relaxed text-gray-800">
                                    <p>
                                        Thank you for joining our{' '}
                                        <span className="font-semibold text-gray-900">
                                            From Project to Conference 101: Abstract and Presentation Workshop
                                        </span>{' '}
                                        last Thursday! 🎉🎉
                                    </p>
                                    <p>
                                        We hope you gained some tips and insight into transforming your project into a
                                        compelling conference submission! ✨
                                    </p>
                                    <p>
                                        A huge thank you to our guest speaker, Odo Tse, for sharing insights and expertise
                                        in identifying and preparing for conferences! 👏🏻🙌🏻
                                    </p>
                                    <p>
                                        Also, special thanks to{' '}
                                        <a
                                            href="https://www.instagram.com/commoncorehku/"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:text-blue-700 font-medium"
                                        >
                                            @commoncorehku
                                        </a>{' '}
                                        for the venue and support! 🙌🏻🙌🏻
                                    </p>
                                    <p>Stay tuned for our upcoming conference! 💪🏻</p>
                                    <p className="text-xs text-gray-500 pt-1">
                                        #TITANProject #CORTEX2026 #commoncore #millenniumfellowship
                                    </p>
                                </div>
                                <div className="border-t border-gray-200 bg-gray-50 px-4 py-3 flex items-center justify-between">
                                    <span className="text-xs text-gray-500">View full post on Instagram</span>
                                    <a
                                        href={INSTAGRAM_POST_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs font-semibold text-blue-600 hover:text-blue-700"
                                    >
                                        Open in Instagram
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* Presentation slides */}
                        <div className="max-w-4xl mx-auto">
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">Presentation Slides</h3>
                            <p className="text-sm text-gray-600 mb-4">
                                Workshop deck (PDF). Thumbnail below shows the opening slide; use download or open for the
                                full deck.
                            </p>
                            <div className="flex flex-wrap gap-3 mb-4">
                                <a
                                    href={slidesPath}
                                    download
                                    className="inline-flex items-center justify-center rounded-full border-2 border-amber-500 px-5 py-2.5 text-sm font-semibold text-amber-800 hover:bg-amber-50 transition"
                                >
                                    Download slides (PDF)
                                </a>
                                <a
                                    href={slidesPathEncoded}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center justify-center rounded-full border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:border-amber-400 hover:text-amber-700 transition"
                                >
                                    Open PDF in new tab
                                </a>
                            </div>
                            <div className="rounded-lg overflow-hidden border border-gray-200 shadow-md bg-gray-50">
                                {!slidesThumbError ? (
                                    <a
                                        href={slidesPathEncoded}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
                                        aria-label="Open full presentation PDF (thumbnail shows first slide)"
                                    >
                                        <img
                                            src={slidesThumbnailEncoded}
                                            alt="Opening slide of the workshop presentation deck"
                                            className="w-full h-auto object-contain max-h-[min(60vh,560px)] mx-auto"
                                            onError={() => setSlidesThumbError(true)}
                                        />
                                    </a>
                                ) : (
                                    <div className="p-8 text-center text-gray-600 text-sm">
                                        <p className="mb-3">
                                            Thumbnail could not be loaded. Open or download the PDF for the full deck.
                                        </p>
                                        <a
                                            href={slidesPathEncoded}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="font-semibold text-amber-700 hover:text-amber-800"
                                        >
                                            Open PDF →
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </AnimatedSection>
            </div>
        </div>
    );
};

export default Workshop2026;
